// Shared bulk-ops machinery used by Sources, WatermarkEncodeLibrary,
// DCPLibrary, and DCPDistribution. Each page used to hand-roll the
// same concurrency-bounded fan-out, the same progress-state shape,
// the same select-all-visible helpers — exactly the kind of
// repetition that drifts when one page gets fixed and the others
// don't. This hook is the single source of truth.
//
// The hook owns:
//   - the Set of selected ids
//   - busy + progress (live { done, total, failed } counter)
//   - the runner (per-target action with concurrency cap)
//   - select-all-visible helpers (operator's "everything I can see")
//
// Per-page concerns the hook deliberately does NOT own:
//   - which list to refetch on completion (passed via `onComplete`)
//   - how to surface errors (passed via `onError`)
//   - the shape of the per-action target (the caller passes any
//     id-shaped value into `toggleSelected`)
import { useState, useCallback } from 'react';

export const DEFAULT_BULK_CONCURRENCY = 4;

export function useBulkRunner({
  concurrency = DEFAULT_BULK_CONCURRENCY,
  onComplete,
  onError,
} = {}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total, failed } | null

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Visible-set helpers — caller passes the currently-rendered row
  // ids so "select all" reflects the operator's filter / sort.
  const allVisibleSelected = useCallback((visibleIds) => {
    const list = Array.isArray(visibleIds) ? visibleIds : [];
    return list.length > 0 && list.every((id) => selectedIds.has(id));
  }, [selectedIds]);

  const someVisibleSelected = useCallback((visibleIds) => {
    const list = Array.isArray(visibleIds) ? visibleIds : [];
    return list.some((id) => selectedIds.has(id));
  }, [selectedIds]);

  const toggleSelectAllVisible = useCallback((visibleIds) => {
    const list = Array.isArray(visibleIds) ? visibleIds : [];
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allHere = list.length > 0 && list.every((id) => next.has(id));
      if (allHere) list.forEach((id) => next.delete(id));
      else list.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  // Run a per-target action against every selected id. CONCURRENCY
  // workers pull from a shared cursor; failures are tallied but
  // don't abort the rest of the batch. Caller's `onComplete` runs
  // before the busy/progress state clears so they can dispatch a
  // refetch synchronously with the "all done" moment.
  const run = useCallback(async (action) => {
    const targets = Array.from(selectedIds);
    if (targets.length === 0) return { done: 0, total: 0, failed: 0 };

    setBusy(true);
    setProgress({ done: 0, total: targets.length, failed: 0 });

    let done = 0;
    let failed = 0;
    let cursor = 0;
    const worker = async () => {
      while (cursor < targets.length) {
        const i = cursor;
        cursor += 1;
        const id = targets[i];
        try {
          await action(id);
        } catch (_e) {
          failed += 1;
        }
        done += 1;
        setProgress({ done, total: targets.length, failed });
      }
    };
    await Promise.all(
      Array.from({ length: Math.max(1, concurrency) }, worker),
    );

    const result = { done, total: targets.length, failed };
    setSelectedIds(new Set());
    setBusy(false);
    setProgress(null);
    if (onComplete) {
      try { onComplete(result); } catch (_e) { /* swallow */ }
    }
    if (failed > 0 && onError) {
      try { onError(result); } catch (_e) { /* swallow */ }
    }
    return result;
  }, [selectedIds, concurrency, onComplete, onError]);

  return {
    selectedIds,
    toggleSelected,
    clearSelection,
    allVisibleSelected,
    someVisibleSelected,
    toggleSelectAllVisible,
    busy,
    progress,
    run,
  };
}
